import { useState } from 'react';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { AlertCircle, KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useResetPassword } from '@/hooks/use-auth';
import { isAuthenticated } from '@/lib/auth-store';

interface ResetSearch {
  token?: string;
}

export const Route = createFileRoute('/reset-password')({
  validateSearch: (s: Record<string, unknown>): ResetSearch => ({
    token: typeof s.token === 'string' ? s.token : undefined,
  }),
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/' });
    }
  },
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const reset = useResetPassword();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const valid = Boolean(token) && password.length >= 8 && password === confirm;
  const errorMessage = reset.error instanceof Error ? reset.error.message : null;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    reset.mutate(
      { token, password },
      {
        onSuccess: () => {
          toast.success('Contraseña actualizada. Inicia sesión.');
          void navigate({ to: '/login' });
        },
      },
    );
  }

  return (
    <div className="grid min-h-screen place-items-center bg-blaze-base px-4 text-foreground">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <img src="/blaze-mark.svg" alt="BlazeIPAM" className="size-10" />
          <h1 className="text-lg font-bold tracking-tight">Nueva contraseña</h1>
        </div>

        <div className="rounded-lg border border-border bg-blaze-elev1 p-5 shadow-xl">
          {!token ? (
            <p className="text-center text-xs text-muted-foreground">
              El enlace de restablecimiento está incompleto o es inválido.
            </p>
          ) : (
            <form onSubmit={onSubmit} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="password">Nueva contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="mín. 8 caracteres"
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm">Confirmar</Label>
                <Input
                  id="confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
              {confirm.length > 0 && password !== confirm && (
                <p className="text-xs text-destructive">Las contraseñas no coinciden.</p>
              )}
              {errorMessage && (
                <p className="flex items-center gap-1.5 text-xs text-destructive">
                  <AlertCircle className="size-3.5" />
                  {errorMessage}
                </p>
              )}
              <Button type="submit" className="w-full gap-2" disabled={!valid || reset.isPending}>
                {reset.isPending ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
                Actualizar contraseña
              </Button>
            </form>
          )}

          <p className="mt-4 text-center text-xs text-muted-foreground">
            <Link to="/login" className="font-medium text-primary hover:underline">
              Volver a iniciar sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
